import { z } from 'zod';
import { type BusinessCardContentSource, hasBusinessCardContent, optionalBlankText } from './business-card';
import { BusinessCardTagsDto, BusinessCardTagsPatch } from './business-card-tags';
import { BUSINESS_INTEREST_CATALOG, BUSINESS_INTEREST_SLUGS } from './business-interests';
import { GroupPhotoDto } from './group-photos';
import { INTEREST_CATALOG, INTEREST_SLUGS, MAX_PERSONAL_INTERESTS } from './interests';
import { PublicPersonalityCodeDto } from './personality-code';
import { PsychTestsSummaryDto } from './psych-tests';
import {
  ConnectionCardBackDto,
  LookingForSlug,
  OfferingSlug,
  PublicCardRankDto,
  PublicCardTheme,
  PublicProfileShowcaseDto,
} from './public-card';

/**
 * Намерение знакомства. Один и тот же человек ищет по-разному: PERSONAL — личные
 * (романтические) знакомства, BUSINESS — деловые. Хранится строкой `NetworkingIntent.mode`
 * и входит в уникальные ключи свайпов и матчей (решение об одном человеке может
 * расходиться по режимам).
 */
export const NetworkingMode = z.enum(['PERSONAL', 'BUSINESS']);
export type NetworkingMode = z.infer<typeof NetworkingMode>;

/**
 * Режим в запросе. Отсутствие параметра = PERSONAL: это и обратная совместимость с
 * нативным `apps/mobile` (у пользователей остаются старые сборки), и правда о прошлом —
 * всё, что накоплено до двух режимов, личное.
 *
 * Регистр не важен: в ссылках параметры пишут строчными (`?mode=business`), а в БД и DTO
 * значение остаётся прописным. Требовать `?mode=BUSINESS` — ловушка для клиента.
 */
export const NetworkingModeQuery = z
  .preprocess((v) => (typeof v === 'string' ? v.toUpperCase() : v), NetworkingMode)
  .default('PERSONAL');

/** Слаги интересов, допустимые в режиме: личные — хобби, деловые — сферы. */
export function interestSlugsForMode(mode: NetworkingMode): readonly string[] {
  return mode === 'BUSINESS' ? BUSINESS_INTEREST_SLUGS : INTEREST_SLUGS;
}

/** Каталог `{ slug, label }` режима — для пикера интересов. */
export function interestCatalogForMode(mode: NetworkingMode): ReadonlyArray<{ slug: string; label: string }> {
  return mode === 'BUSINESS' ? BUSINESS_INTEREST_CATALOG : INTEREST_CATALOG;
}

/**
 * Любой известный слаг интереса — объединение словарей. Нужен там, где схема не знает
 * режима (тело `PATCH`, где режим приезжает query-параметром): мусор отсекается здесь,
 * а «слаг не того режима» — уже сервисом, с 422.
 */
export const AnyInterestSlug = z.enum([...INTEREST_SLUGS, ...BUSINESS_INTEREST_SLUGS]);
export type AnyInterestSlug = z.infer<typeof AnyInterestSlug>;

/** Разбор CSV-фильтра интересов: неизвестное и чужое для режима молча отбрасывается. */
export function parseInterestCsv(raw: string | undefined, mode: NetworkingMode): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((slug) => slug.trim())
    .filter((slug) => isInterestOfMode(slug, mode));
}

/**
 * Скоуп листингов матчей/лайков. `ALL` есть только здесь: «Мой круг» — это люди, а не
 * режимы, и один человек может быть в круге по обеим причинам.
 */
export const MatchScope = z.enum(['PERSONAL', 'BUSINESS', 'ALL']);
export type MatchScope = z.infer<typeof MatchScope>;
export const MatchScopeQuery = z
  .preprocess((v) => (typeof v === 'string' ? v.toUpperCase() : v), MatchScope)
  .default('PERSONAL');

/** Принадлежит ли слаг словарю режима. Проверяется на ЗАПИСЬ, не на чтение. */
export function isInterestOfMode(slug: string, mode: NetworkingMode): boolean {
  return (interestSlugsForMode(mode) as readonly string[]).includes(slug);
}

/**
 * Какие значения «кого ищу» показываем в пикере режима. Словарь общий (см.
 * `LookingForSlug`), различается только видимость: «Свидания» в деловом пикере и
 * «Инвестиции» в личном — мусор. Карта применяется ТОЛЬКО на запись: у людей в визитке
 * уже лежат значения, выбранные до появления режимов, и фильтр на чтении их потерял бы.
 */
export const LOOKING_FOR_BY_MODE: Record<NetworkingMode, readonly LookingForSlug[]> = {
  PERSONAL: ['friends', 'dates', 'travel', 'collaborators'],
  BUSINESS: ['collaborators', 'clients', 'investment', 'team', 'mentorship'],
};

/** То же для «что даю». */
export const OFFERING_BY_MODE: Record<NetworkingMode, readonly OfferingSlug[]> = {
  PERSONAL: ['show_city', 'host', 'teach'],
  BUSINESS: ['work_together', 'teach', 'invest'],
};

export const SwipeDirection = z.enum(['LIKE', 'PASS']);
export type SwipeDirection = z.infer<typeof SwipeDirection>;

export const PhotoModeration = z.enum(['PENDING', 'APPROVED', 'HIDDEN_PERMANENT']);
export type PhotoModeration = z.infer<typeof PhotoModeration>;

/**
 * Минимум интересов для участия в знакомствах (легаси-гейт «≥3, чтобы действовать
 * и показываться»). Совпадает с MAX_INTERESTS — слоты интересов должны быть заполнены.
 */
export const MIN_INTERESTS_FOR_NETWORKING = 3;

/** Пол в анкете знакомств. Nullable в профиле, пока человек не заполнил. */
export const Gender = z.enum(['woman', 'man', 'other']);
export type Gender = z.infer<typeof Gender>;

export const MAX_SEEKING_GENDERS = 3;

/**
 * Стабильный пол массовки: чётный первый ниббл uuid → woman, иначе man. Ту же
 * формулу повторяет бэкфилл в миграции — фейк, созданный до и после неё, обязан
 * получать одинаковый пол.
 *
 * `other` в `seekingGenders` — не про ориентацию фиктивной анкеты, а про то,
 * чтобы массовка не исчезала у зрителя с полом `other`: взаимный фильтр требует
 * от кандидата искать пол зрителя, и колода из одних гетеро выключала бы
 * знакомства всем, кто выбрал этот чип.
 */
export function fakeGenderFromUserId(userId: string): { gender: Gender; seekingGenders: Gender[] } {
  const hex = userId.replace(/-/g, '');
  const nibble = Number.parseInt(hex.slice(0, 1) || '0', 16);
  const woman = Number.isFinite(nibble) && nibble % 2 === 0;
  return woman
    ? { gender: 'woman', seekingGenders: ['man', 'other'] }
    : { gender: 'man', seekingGenders: ['woman', 'other'] };
}

/** Кап открытых партнёрских предложений на человека. */
export const PARTNER_OFFER_OPEN_MAX = 3;
/** Порог триграммного сходства партнёрских предложений в одном городе. */
export const PARTNER_OFFER_MIN_SIMILARITY = 0.35;

/**
 * Бесплатных свайпов в день, далее — NETWORKING_SWIPE_COST за свайп (любого
 * направления: и LIKE, и PASS). Лимит дневной — сбрасывается на границе суток.
 */
export const NETWORKING_FREE_SWIPES = 5;
export const NETWORKING_SWIPE_COST = 1;

/** @param usedToday — сколько НОВЫХ свайпов юзер сделал в текущих сутках. */
/**
 * Квота свайпов режима. В деловом режиме лайки бесплатны и счётчик не применяется
 * (решение продукта: в деле нужен объём контактов, в романтике ценность в дефиците).
 * Отдаём это флагом `isFree`, а не сентинел-числом «999 бесплатных»: клиент обязан
 * различать «лимит кончился» и «лимита нет», иначе кнопка снова начнёт врать про цену.
 */
export function computeNetworkingSwipeQuota(usedToday: number, mode: NetworkingMode = 'PERSONAL') {
  if (mode === 'BUSINESS') {
    return { usedToday: 0, freeRemaining: 0, nextSwipeCost: 0, isFree: true };
  }
  const freeRemaining = Math.max(0, NETWORKING_FREE_SWIPES - usedToday);
  return {
    usedToday,
    freeRemaining,
    nextSwipeCost: freeRemaining > 0 ? 0 : NETWORKING_SWIPE_COST,
    isFree: false,
  };
}

export const NetworkingSwipeQuotaDto = z.object({
  usedToday: z.number().int().min(0),
  freeRemaining: z.number().int().min(0),
  nextSwipeCost: z.number().int().min(0),
  /** Баланс с учётом незавершённых PENDING-списаний — как на сервере при свайпе. */
  energyAvailable: z.number().int().min(0),
  /**
   * Лайки режима бесплатны, дневной счётчик к ним не применяется (деловой режим).
   * `false` по умолчанию — старые клиенты поля не видят и продолжают читать
   * `freeRemaining`/`nextSwipeCost`, как раньше.
   */
  isFree: z.boolean().default(false),
});
export type NetworkingSwipeQuotaDto = z.infer<typeof NetworkingSwipeQuotaDto>;

/**
 * Готовность профиля к знакомствам: одобренное фото + заполненная режимом часть.
 * Юзер с готовым профилем показывается в чужих deck'ах; свайпать достаточно
 * той же режимной части (фото для свайпа не требуется — см. networking-swipe.service).
 *
 * Личный режим считает интересы (≥3), деловой — заполненность бизнес-карты:
 * сферы делового интента — теги каталога знакомств, форма их больше не
 * показывает, и порог на них навсегда закрыл бы деловую выдачу новым анкетам.
 * `card` в личном режиме не смотрится, но передаётся всегда — чтобы вызывающий
 * не решал за функцию, какой режим что читает.
 */
export function isNetworkingReady(input: {
  photoModeration: PhotoModeration;
  mode: NetworkingMode;
  interestsCount: number;
  card: BusinessCardContentSource;
}): boolean {
  if (input.photoModeration !== 'APPROVED') return false;
  return input.mode === 'BUSINESS'
    ? hasBusinessCardContent(input.card)
    : input.interestsCount >= MIN_INTERESTS_FOR_NETWORKING;
}

export const AcquaintanceRequestInput = z.object({
  targetUserId: z.uuid(),
});
export type AcquaintanceRequestInput = z.infer<typeof AcquaintanceRequestInput>;

export const SwipeInput = z.object({
  targetUserId: z.uuid(),
  direction: SwipeDirection,
  /** Режим — часть записываемого решения, поэтому в теле, а не в query. */
  mode: NetworkingModeQuery,
});
export type SwipeInput = z.infer<typeof SwipeInput>;

/**
 * Правка анкеты. Общая часть (`displayName`, `birthDate`) пишется в личность, режимная —
 * в интент режима из query-параметра. Принадлежность слагов режиму проверяет сервис (422):
 * схема тела режима не знает.
 */
export const NetworkingProfileUpdateInput = z.object({
  // общая часть — одна на человека
  displayName: z.string().trim().min(1).max(64).nullable().optional(),
  birthDate: z.iso.date().nullable().optional(),
  gender: Gender.nullable().optional(),
  bioRole: optionalBlankText(100),
  bioSkills: optionalBlankText(250),
  bioIndustry: optionalBlankText(150),
  bioRequest: optionalBlankText(250),
  ...BusinessCardTagsPatch.shape,
  // режимная часть
  isDiscoverable: z.boolean().optional(),
  interests: z.array(z.string().trim().min(1).max(80)).max(MAX_PERSONAL_INTERESTS).optional(),
  lookingFor: z.array(LookingForSlug).max(3).optional(),
  offering: z.array(OfferingSlug).max(3).optional(),
  seekingGenders: z.array(Gender).max(MAX_SEEKING_GENDERS).optional(),
  /** `null` — снять оверрайд и вернуться к общему «о себе»; строка — переопределить в режиме. */
  bio: z.string().max(280).nullable().optional(),
});
export type NetworkingProfileUpdateInput = z.infer<typeof NetworkingProfileUpdateInput>;

export const DeckQuery = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
  mode: NetworkingModeQuery,
});
export type DeckQuery = z.infer<typeof DeckQuery>;

/**
 * Пагинация и фильтры каталога `/people` — не смешивать с колодой свайпов.
 * Поиск и интересы фильтруют на СЕРВЕРЕ (до нарезки страницы): клиент видит
 * только загруженные страницы, и клиентский фильтр отвечал бы «никого не
 * нашли» при полном каталоге.
 */
export const DirectoryQuery = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
  cursor: z.string().optional(),
  /** Свободный поиск: имя, био, город, подписи интересов. */
  q: z.string().trim().max(64).optional(),
  /**
   * Слаги интересов через запятую (ИЛИ). Разбор — `parseInterestCsv` в контроллере:
   * словарь зависит от `mode`, а поле схемы соседнего поля не видит. Object-level
   * `transform` здесь не годится — `createZodDto` строит из схемы OpenAPI и требует форму.
   */
  interests: z.string().optional(),
  mode: NetworkingModeQuery,
});
export type DirectoryQuery = z.infer<typeof DirectoryQuery>;

export const PublicProfileDto = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  bio: z.string().nullable(),
  photoUrl: z.string().nullable(),
  age: z.number().int().min(0).max(150).nullable(),
  // Название города кандидата — показывается на карточке дека (deck показывает свой
  // город в приоритете, затем другие; город помогает понять, откуда человек).
  // null, если город у юзера не задан.
  cityName: z.string().nullable(),
  /** Интересы запрошенного режима: личные хобби (свои теги тоже) либо деловые сферы. */
  interests: z.array(z.string()),
  /**
   * VIP активен на момент ответа. Срок считает сервер (`isVipActive`), наружу
   * уходит только флаг: даты выдачи и истечения — внутренние поля `User`, и на
   * чужом профиле им делать нечего (то же решение, что в `GET /me`).
   */
  isVip: z.boolean(),
  /** Бейджи на фото в каталоге людей. Нет поля — бейджей нет. */
  statuses: z.array(z.object({ tier: z.enum(['pro', 'gold', 'invest']) })).optional(),
  /** Логотип шапки визитки. Нет поля или null — логотип UpMe. Колода поле не шлёт. */
  logoUrl: z.string().nullable().optional(),
  /** Тема пластика. Нет поля — «Наша». Колода поле не шлёт. */
  theme: PublicCardTheme.optional(),
  /**
   * Публичный Код личности; null, пока не собраны все три сигнала. Сырых
   * результатов методик тут нет намеренно — чужому клиенту их не отдаём.
   */
  personalityCode: PublicPersonalityCodeDto.nullable().default(null),
  /** Только когда профиль смотрит admin; иначе omit. */
  energyAvailable: z.number().int().min(0).optional(),
  /**
   * Оборот Connection Card. Только GET /users/:id — колода знакомств поле не шлёт.
   * Обязательным не делаем: `toPublicProfileDto` дека не раздуваем.
   */
  cardBack: ConnectionCardBackDto.optional(),
  /**
   * Платформенный ранг на лице Connection Card. Только GET /users/:id —
   * та же шкала, что на экране «Я» и HTML-визитке (`resolvePlatformStatus`).
   */
  rank: PublicCardRankDto.optional(),
  /**
   * Блоки под CTA совместимости. Только GET /users/:id — колода и чат не шлют.
   */
  showcase: PublicProfileShowcaseDto.optional(),
});
export type PublicProfileDto = z.infer<typeof PublicProfileDto>;

export const NetworkingDeckResponse = z.object({
  candidates: z.array(PublicProfileDto),
  quota: NetworkingSwipeQuotaDto,
});
export type NetworkingDeckResponse = z.infer<typeof NetworkingDeckResponse>;

/** Каталог людей (не колода свайпов): без исключения уже просмотренных. */
export const PeopleDirectoryResponse = z.object({
  items: z.array(PublicProfileDto),
  total: z.number().int().min(0),
  nextCursor: z.string().nullable(),
  /** Живые + 2000 + ход часов, то же число, что в футере визитки. Шапка «Люди», не размер страницы. */
  audience: z.number().int().min(0),
});
export type PeopleDirectoryResponse = z.infer<typeof PeopleDirectoryResponse>;

/** Сводка по режиму — из неё клиент решает, показывать переключатель или приглашение. */
export const NetworkingModeSummaryDto = z.object({
  mode: NetworkingMode,
  /** Интент создан, то есть режим когда-либо заполнялся. */
  exists: z.boolean(),
  isDiscoverable: z.boolean(),
  isReady: z.boolean(),
  interestsCount: z.number().int().min(0),
});
export type NetworkingModeSummaryDto = z.infer<typeof NetworkingModeSummaryDto>;

export const NetworkingProfileDto = z.object({
  userId: z.uuid(),
  /** Режим, срез которого отдан. */
  mode: NetworkingMode,
  isDiscoverable: z.boolean(),
  // Имя-оверрайд, заданное юзером (null → показывается имя из Telegram).
  displayName: z.string().nullable(),
  bio: z.string().nullable(),
  /** Свободная бизнес-карта. Общая на человека, не зависит от режима анкеты. */
  bioRole: z.string().nullable(),
  bioSkills: z.string().nullable(),
  bioIndustry: z.string().nullable(),
  bioRequest: z.string().nullable(),
  ...BusinessCardTagsDto.shape,
  birthDate: z.iso.date().nullable(),
  gender: Gender.nullable(),
  photoUrl: z.string().nullable(),
  // Статус модерации своего фото знакомств + причина скрытия (null, если фото нет
  // или причина не указана). Видны только владельцу в своём профиле.
  photoModeration: PhotoModeration,
  moderationComment: z.string().nullable(),
  /** true — юзер загружал фото в анкету знакомств (не TG-fallback). */
  hasUploadedNetworkingPhoto: z.boolean(),
  /** Интересы ЭТОГО режима. В личном — каталог и свои теги, порядок это важность. */
  interests: z.array(z.string()),
  lookingFor: z.array(LookingForSlug),
  offering: z.array(OfferingSlug),
  seekingGenders: z.array(Gender),
  /** `bio` переопределён в этом режиме (иначе показан общий текст личности). */
  bioOverridden: z.boolean(),
  // Готов ли профиль к знакомствам (фото APPROVED + ≥3 интереса) — по ЭТОМУ режиму.
  // Используется фронтом для гейта/чеклиста на входе в знакомства.
  isNetworkingReady: z.boolean(),
  /** Всегда два элемента — личный и деловой; питает переключатель режимов. */
  modes: z.array(NetworkingModeSummaryDto).length(2),
  psychTests: PsychTestsSummaryDto,
  personalityCode: PublicPersonalityCodeDto.nullable().default(null),
});
export type NetworkingProfileDto = z.infer<typeof NetworkingProfileDto>;

export const SwipeResultDto = z.object({
  match: z.boolean(),
  matchedUser: PublicProfileDto.optional(),
});
export type SwipeResultDto = z.infer<typeof SwipeResultDto>;

export const MatchDto = z.object({
  matchId: z.uuid(),
  user: PublicProfileDto,
  /** Режим, в котором сложился матч: одна пара может совпасть в обоих. */
  mode: NetworkingMode,
  createdAt: z.iso.datetime(),
});
export type MatchDto = z.infer<typeof MatchDto>;

/** Группа пересечений: событие + фото, где viewer отмечен организатором. */
export const IntersectionEventGroupDto = z.object({
  eventId: z.uuid(),
  eventTitle: z.string(),
  eventStartsAt: z.iso.datetime(),
  eventKind: z.enum(['EVENT', 'MEETING']),
  coverUrl: z.string().nullable(),
  photos: z.array(GroupPhotoDto),
});
export type IntersectionEventGroupDto = z.infer<typeof IntersectionEventGroupDto>;

export const IntersectionsListResponse = z.array(IntersectionEventGroupDto);
export type IntersectionsListResponse = z.infer<typeof IntersectionsListResponse>;
